import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { UserCog, Globe, Bell, Palette, Save } from 'lucide-react';
import { toast } from 'sonner';

const UserPreferences = () => {
  const [preferences, setPreferences] = useState({
    language: 'en-US',
    timezone: 'America/New_York',
    dateFormat: 'MM/DD/YYYY',
    theme: 'dark',
    notifications: {
      lowStock: true,
      orderUpdates: true,
      weeklyReports: false,
      securityAlerts: true
    }
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    // Simulate save
    await new Promise(resolve => setTimeout(resolve, 500));
    toast.success('Preferences saved');
    setSaving(false);
  };

  return (
    <div className="space-y-3">
      {/* Display Settings */}
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="p-3">
          <div className="flex items-center gap-2 mb-3">
            <Globe className="w-3.5 h-3.5 text-blue-400" />
            <h3 className="text-xs font-semibold text-white">Display Settings</h3>
          </div>
          <p className="text-[10px] text-gray-400 mb-3">Configure language, timezone, and format preferences</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase text-gray-400 mb-1 block">Language</label>
              <select 
                className="w-full h-7 px-2 text-xs bg-gray-900 border border-gray-600 rounded text-white"
                value={preferences.language}
                onChange={(e) => setPreferences({...preferences, language: e.target.value})}
              >
                <option value="en-US">English (US)</option>
                <option value="en-UK">English (UK)</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
              </select>
            </div>
            
            <div>
              <label className="text-[10px] uppercase text-gray-400 mb-1 block">Timezone</label>
              <select 
                className="w-full h-7 px-2 text-xs bg-gray-900 border border-gray-600 rounded text-white"
                value={preferences.timezone}
                onChange={(e) => setPreferences({...preferences, timezone: e.target.value})}
              >
                <option value="America/New_York">Eastern (EST)</option>
                <option value="America/Chicago">Central (CST)</option>
                <option value="America/Denver">Mountain (MST)</option>
                <option value="America/Los_Angeles">Pacific (PST)</option>
                <option value="Europe/London">London (GMT)</option>
              </select>
            </div>
            
            <div>
              <label className="text-[10px] uppercase text-gray-400 mb-1 block">Date Format</label>
              <select 
                className="w-full h-7 px-2 text-xs bg-gray-900 border border-gray-600 rounded text-white"
                value={preferences.dateFormat}
                onChange={(e) => setPreferences({...preferences, dateFormat: e.target.value})}
              >
                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Theme Settings */}
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="p-3">
          <div className="flex items-center gap-2 mb-3">
            <Palette className="w-3.5 h-3.5 text-purple-400" />
            <h3 className="text-xs font-semibold text-white">Theme</h3>
          </div>
          <p className="text-[10px] text-gray-400 mb-3">Choose your preferred color theme</p>
          
          <div className="flex gap-2">
            <button
              className={`px-3 py-1.5 text-xs rounded border transition-colors ${
                preferences.theme === 'dark' 
                  ? 'bg-blue-600 border-blue-500 text-white' 
                  : 'bg-gray-900 border-gray-600 text-gray-400 hover:text-white'
              }`}
              onClick={() => setPreferences({...preferences, theme: 'dark'})}
            >
              🌙 Dark
            </button>
            <button
              className={`px-3 py-1.5 text-xs rounded border transition-colors ${
                preferences.theme === 'light' 
                  ? 'bg-blue-600 border-blue-500 text-white' 
                  : 'bg-gray-900 border-gray-600 text-gray-400 hover:text-white'
              }`}
              onClick={() => setPreferences({...preferences, theme: 'light'})}
              disabled
            >
              ☀️ Light (Coming Soon)
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Notification Settings */}
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="p-3">
          <div className="flex items-center gap-2 mb-3">
            <Bell className="w-3.5 h-3.5 text-yellow-400" />
            <h3 className="text-xs font-semibold text-white">Email Notifications</h3>
          </div>
          <p className="text-[10px] text-gray-400 mb-3">Choose which notifications you want to receive</p>
          
          <div className="space-y-2">
            {[
              { key: 'lowStock', label: 'Low stock alerts', desc: 'Get notified when products are running low' },
              { key: 'orderUpdates', label: 'Order updates', desc: 'Updates about order status changes' },
              { key: 'weeklyReports', label: 'Weekly reports', desc: 'Weekly summary of your business metrics' },
              { key: 'securityAlerts', label: 'Security alerts', desc: 'Important security notifications' },
            ].map(item => (
              <label key={item.key} className="flex items-start gap-2 cursor-pointer group">
                <input 
                  type="checkbox" 
                  checked={preferences.notifications[item.key]}
                  onChange={(e) => setPreferences({
                    ...preferences, 
                    notifications: {...preferences.notifications, [item.key]: e.target.checked}
                  })}
                  className="mt-0.5 rounded border-gray-600 bg-gray-900 text-blue-600"
                />
                <div>
                  <span className="text-xs text-gray-300 group-hover:text-white">{item.label}</span>
                  <p className="text-[10px] text-gray-500">{item.desc}</p>
                </div>
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="h-7 px-3 text-xs bg-blue-600 hover:bg-blue-700"
        >
          {saving ? 'Saving...' : <><Save className="w-3 h-3 mr-1" /> Save Preferences</>}
        </Button>
      </div>
    </div>
  );
};

export default UserPreferences;
